import { Fragment, useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  Button,
  Checkbox,
  EntityCard,
  Icon,
  SearchInput,
  Spinner,
  Tag,
  ckConfirm,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  aiDataAuditApi,
  type AuditChatTurn,
  type AuditCheckDto,
  type AuditFixActionDto,
  type AuditFixDto,
  type AuditItemDto,
} from "../../../api/aiDataAuditApi";
import { loadAssistantConfigured } from "../../assistant/AssistantPanel";
import { notifyDataChanged } from "../../events";
import { useShell } from "../../shell/ShellContext";
import { AgentChat, type AgentChatAction, type AgentChatMessage } from "../shared/AgentChat";
import "./dataAudit.css";

/**
 * DataAuditAgent — «בדיקת נתונים» (ADR-0035, Turbo's dataEntry/DataAuditAgent): the agent goes over the
 * organization's live data and finds the gaps — deals whose «paid» does not match the payments, totals
 * that do not match the price, duplicates, deals without a rep, leads not linked to their customer,
 * payment methods and products saved as text, customers without contact details… — explains each one,
 * and fixes what the user approves (every fix can be undone). The record-card language: header + KPIs,
 * tabs, side column.
 */
const PAGE = 200;
const AUDIT_STARTERS = [
  "תעבור על כל הנתונים ותגיד לי מה הכי דחוף לתקן",
  "הגבייה לא מסתדרת — יש עסקאות ש«שולם» בהן לא תואם לתשלומים?",
  "יש עסקאות או תשלומים כפולים?",
  "למה דוח העמלות לא נכון?",
];
let seq = 0;
const nextId = () => {
  seq += 1;
  return `a${seq}`;
};

const errorText = (e: unknown, fallback: string) => (e as { message?: string })?.message || fallback;
export const money = (n: number | null | undefined) =>
  n === null || n === undefined || Number.isNaN(Number(n)) ? "—" : `₪${Number(n).toLocaleString("he-IL", { maximumFractionDigits: 2 })}`;
export const shortDate = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};
const he = (n: number) => n.toLocaleString("he-IL");

/* ── record-card side parts (Turbo's DataEntryWorkspace DeSection / KV) ── */
function DeSection({ title, children, defaultOpen = true }: { title: ReactNode; children: ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`ck-sec ${open ? "" : "is-closed"}`}>
      <div className="ck-sec__h">
        <button type="button" className="ck-sec__toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Icon name="down" size={14} className="ck-sec__chev" />
          <b>{title}</b>
        </button>
      </div>
      {open ? <div className="ck-sec__b">{children}</div> : null}
    </div>
  );
}

function KV({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  const shown = items.filter((x) => x.value !== null && x.value !== undefined && x.value !== "");
  if (!shown.length) return <div className="ck-sec__muted">אין נתונים</div>;
  return (
    <dl className="ck-kv">
      {shown.map((x) => (
        <Fragment key={x.label}>
          <dt>{x.label}</dt>
          <dd>{x.value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

function riskTag(c: AuditCheckDto) {
  return c.risk === "review" ? <Tag tone="warning">לבדיקה לפני תיקון</Tag> : <Tag tone="success">תיקון בטוח</Tag>;
}

type ItemState = {
  items?: AuditItemDto[];
  total?: number;
  sum?: number;
  text?: string;
  selected?: Set<string>;
  loading?: boolean;
};

type FixOpts = { ids?: string[]; recommended?: boolean; count: number; amount: number };

function CheckCard({
  c,
  open,
  onToggle,
  state,
  onLoadMore,
  onSearch,
  onSelect,
  onFix,
  onOpenItem,
  busy,
}: {
  c: AuditCheckDto;
  open: boolean;
  onToggle: () => void;
  state?: ItemState;
  onLoadMore: () => void;
  onSearch: (text: string) => void;
  onSelect: (how: "recommended" | "all" | "none" | "add" | "remove", id?: string) => void;
  onFix: (opts: FixOpts) => void;
  onOpenItem: (item: AuditItemDto) => void;
  busy: boolean;
}) {
  const items = state?.items ?? [];
  const selected = state?.selected ?? new Set<string>();
  const fixable = Boolean(c.fixLabel) && c.canFix;
  const picks = fixable && !c.allOrNothing;
  const nSel = c.allOrNothing ? c.count - c.blocked : selected.size;
  return (
    <section className={`de-audit-card ${open ? "is-open" : ""}`} aria-label={c.title}>
      <div className="de-audit-card__h">
        <div className="de-audit-card__t">
          <b>{c.title}</b>
          <div className="de-audit-card__tags">
            <Tag tone={c.count ? "warning" : "success"}>{c.count ? `${he(c.count)}${c.truncated ? "+" : ""} רשומות` : "תקין"}</Tag>
            {c.amount ? <Tag>{money(c.amount)}</Tag> : null}
            {c.count && c.fixLabel ? riskTag(c) : null}
            {c.count && !c.fixLabel ? <Tag tone="neutral">תיקון ידני</Tag> : null}
            {c.count && c.fixLabel && !c.canFix ? <Tag tone="neutral">אין הרשאה לתקן</Tag> : null}
          </div>
        </div>
        <span className="ck-grow" />
        {c.count ? (
          <Button size="sm" variant="ghost" icon={open ? "up" : "down"} onClick={onToggle}>
            {open ? "סגירה" : "פירוט"}
          </Button>
        ) : null}
        {fixable && c.recommended && !c.allOrNothing ? (
          <Button
            size="sm"
            variant="primary"
            icon="check"
            disabled={busy}
            onClick={() => onFix({ recommended: true, count: c.recommended, amount: c.recommendedAmount })}
          >
            {c.fixLabel} · {he(c.recommended)} מומלצות
          </Button>
        ) : null}
        {fixable && c.allOrNothing && c.count - c.blocked > 0 ? (
          <Button size="sm" variant="primary" icon="check" disabled={busy} onClick={() => onFix({ recommended: true, count: c.count - c.blocked, amount: c.amount })}>
            {c.fixLabel} ({he(c.count - c.blocked)})
          </Button>
        ) : null}
      </div>
      {c.count ? <p className="de-audit-card__x">{c.explain}</p> : null}
      {c.count && c.groups?.length ? (
        <div className="de-audit-card__groups">
          {c.groups.map((g) => (
            <span key={g.key} className="de-chip">
              {g.label} · {he(g.count)}
            </span>
          ))}
        </div>
      ) : null}
      {c.facts?.noPaymentMethodList ? <Banner tone="info">אין עדיין רשימת צורות תשלום בהגדרות המכירות — אין מול מה לבדוק.</Banner> : null}
      {c.error ? <Banner tone="danger">הבדיקה נכשלה: {c.error}</Banner> : null}

      {open ? (
        <div className="de-audit-items">
          <div className="de-audit-items__bar">
            <SearchInput placeholder="חיפוש בשם / בפרט" value={state?.text ?? ""} onChange={(v) => onSearch(v)} onClear={() => onSearch("")} />
            {picks ? (
              <>
                <Button size="sm" variant="ghost" onClick={() => onSelect("recommended")}>
                  בחירת המומלצות
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onSelect("all")}>
                  הכל
                </Button>
                <Button size="sm" variant="ghost" onClick={() => onSelect("none")}>
                  ניקוי
                </Button>
              </>
            ) : null}
            <span className="ck-grow" />
            <span className="de-muted">
              {he(state?.total ?? 0)} רשומות · {money(state?.sum ?? 0)}
            </span>
            {picks ? (
              <Button
                size="sm"
                variant="primary"
                icon="check"
                disabled={busy || !nSel}
                onClick={() =>
                  onFix({
                    ids: Array.from(selected),
                    count: nSel,
                    amount: items.filter((i) => selected.has(i.id)).reduce((a, i) => a + Math.abs(i.amountShekels ?? 0), 0),
                  })
                }
              >
                תיקון הנבחרות ({he(nSel)})
              </Button>
            ) : null}
          </div>
          {state?.loading && !items.length ? (
            <div className="de-audit-items__loading">
              <Spinner size={16} /> טוען…
            </div>
          ) : null}
          <div className={`de-audit-table${picks ? "" : " is-nock"}`} role="table">
            {items.map((i) => (
              <div key={`${i.entity}:${i.id}`} role="row" className={`de-audit-row ${i.selectable ? "" : "is-blocked"} ${selected.has(i.id) ? "is-on" : ""}`}>
                {picks ? (
                  <span role="cell" className="de-audit-row__ck">
                    <Checkbox
                      checked={selected.has(i.id)}
                      disabled={!i.selectable}
                      onChange={(on) => onSelect(on ? "add" : "remove", i.id)}
                      label=""
                      aria-label={`בחירה ${i.label}`}
                    />
                  </span>
                ) : null}
                <span role="cell" className="de-audit-row__d">
                  {i.date ? shortDate(i.date) : "—"}
                </span>
                <span role="cell" className="de-audit-row__w">
                  <button type="button" className="ck-link" onClick={() => onOpenItem(i)} title="פתיחת הרשומה">
                    <b>{i.label}</b>
                  </button>
                  <small>{i.detail}</small>
                </span>
                <span role="cell" className="de-audit-row__a">
                  {i.amountShekels === null ? "" : money(i.amountShekels)}
                </span>
                <span role="cell" className="de-audit-row__n">
                  {i.note ? (
                    <Tag tone="warning" icon="alert">
                      {i.note}
                    </Tag>
                  ) : i.preselect ? (
                    <Tag tone="success">מומלץ</Tag>
                  ) : null}
                </span>
              </div>
            ))}
          </div>
          {items.length < (state?.total ?? 0) ? (
            <div className="de-audit-items__more">
              <Button size="sm" onClick={onLoadMore} disabled={state?.loading}>
                עוד {he(Math.min(PAGE, (state?.total ?? 0) - items.length))}…
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export type DataAuditAgentProps = {
  open: boolean;
  onClose?: () => void;
  /** opened by the side assistant with the user's question — asked once, on the conversation tab */
  initialQuestion?: string | null;
};

export function DataAuditAgent({ open, onClose, initialQuestion = null }: DataAuditAgentProps) {
  const toast = useCkToast();
  const navigate = useNavigate();
  const { allowed } = useShell();
  const [tab, setTab] = useState("findings");
  const [checks, setChecks] = useState<AuditCheckDto[]>([]);
  const [fixes, setFixes] = useState<AuditFixDto[]>([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openCode, setOpenCode] = useState<string | null>(null);
  const [itemState, setItemState] = useState<Record<string, ItemState>>({});
  const [busyFix, setBusyFix] = useState(false);
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [aiConfigured, setAiConfigured] = useState(false);
  const changedAny = useRef(false);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canChat = allowed("ai.use") && aiConfigured;

  useEffect(() => {
    if (!allowed("ai.use")) return undefined;
    let alive = true;
    void loadAssistantConfigured().then((v) => alive && setAiConfigured(v));
    return () => {
      alive = false;
    };
  }, [allowed]);

  const run = useCallback(async (codes?: string[]) => {
    setRunning(true);
    setError(null);
    try {
      const got = await aiDataAuditApi.run(codes);
      setChecks((prev) => (codes?.length ? prev.map((c) => got.find((g) => g.code === c.code) ?? c) : got));
      if (codes?.length) {
        setItemState((s) => {
          const n = { ...s };
          codes.forEach((c) => delete n[c]);
          return n;
        });
      } else setItemState({});
    } catch (e) {
      setError(errorText(e, "הבדיקה נכשלה"));
    } finally {
      setRunning(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    aiDataAuditApi
      .state()
      .then((st) => {
        setFixes(st.fixes ?? []);
        if (st.checks?.length) setChecks(st.checks);
        else void run();
      })
      .catch(() => void run());
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadItems = useCallback(
    async (code: string, { append = false, text }: { append?: boolean; text?: string } = {}) => {
      const cur = itemState[code] ?? {};
      const q = text !== undefined ? text : cur.text ?? "";
      const offset = append ? (cur.items ?? []).length : 0;
      setItemState((s) => ({ ...s, [code]: { ...(s[code] ?? {}), loading: true, text: q } }));
      try {
        const d = await aiDataAuditApi.items(code, { offset, limit: PAGE, text: q || undefined });
        setItemState((s) => {
          const prev = s[code] ?? {};
          const items = append ? [...(prev.items ?? []), ...d.items] : d.items;
          // the agent's recommendation is the starting selection
          const selected =
            append && prev.selected ? new Set(prev.selected) : new Set(items.filter((i) => i.preselect && i.selectable).map((i) => i.id));
          if (append) d.items.filter((i) => i.preselect && i.selectable).forEach((i) => selected.add(i.id));
          return { ...s, [code]: { ...prev, items, total: d.total, sum: d.sum, loading: false, selected, text: q } };
        });
      } catch (e) {
        setItemState((s) => ({ ...s, [code]: { ...(s[code] ?? {}), loading: false } }));
        toast.error(errorText(e, "הטעינה נכשלה"));
      }
    },
    [itemState, toast],
  );

  const toggle = (code: string) => {
    if (openCode === code) {
      setOpenCode(null);
      return;
    }
    setOpenCode(code);
    if (!itemState[code]?.items) void loadItems(code);
  };

  const select = (code: string, how: "recommended" | "all" | "none" | "add" | "remove", id?: string) => {
    setItemState((s) => {
      const st = s[code] ?? {};
      const items = st.items ?? [];
      let sel = new Set(st.selected ?? []);
      if (how === "recommended") sel = new Set(items.filter((i) => i.preselect && i.selectable).map((i) => i.id));
      else if (how === "all") sel = new Set(items.filter((i) => i.selectable).map((i) => i.id));
      else if (how === "none") sel = new Set();
      else if (how === "add" && id) sel.add(id);
      else if (how === "remove" && id) sel.delete(id);
      return { ...s, [code]: { ...st, selected: sel } };
    });
  };

  const doFix = useCallback(
    async (check: Pick<AuditCheckDto, "code" | "fixLabel" | "risk" | "title">, { ids, recommended, count, amount }: FixOpts) => {
      const risky = check.risk === "review";
      const label = check.fixLabel ?? "תיקון";
      const ok = await ckConfirm({
        title: `${label}?`,
        message: `${he(count)} רשומות${amount ? ` · ${money(amount)}` : ""}. ${risky ? "זה תיקון שכדאי לבדוק לפני שמאשרים. " : ""}אפשר לבטל אחר כך ב«מה תוקן».`,
        confirmLabel: label,
      });
      if (!ok) return null;
      setBusyFix(true);
      try {
        const { fix, check: after } = await aiDataAuditApi.fix({
          code: check.code,
          ids: ids?.length ? ids : undefined,
          recommended: !ids?.length && recommended ? true : undefined,
        });
        changedAny.current = true;
        setFixes((f) => [fix, ...f.filter((x) => x.id !== fix.id)]);
        if (after) setChecks((cs) => cs.map((c) => (c.code === after.code ? after : c)));
        setItemState((s) => {
          const n = { ...s };
          delete n[check.code];
          return n;
        });
        if (openCode === check.code) setTimeout(() => void loadItems(check.code), 0);
        toast.success(`${he(fix.count)} תוקנו${fix.failed?.length ? ` · ${fix.failed.length} לא` : ""}`);
        return fix;
      } catch (e) {
        toast.error(errorText(e, "התיקון נכשל"));
        return null;
      } finally {
        setBusyFix(false);
      }
    },
    [openCode, loadItems, toast],
  );

  const undoFix = async (fix: AuditFixDto) => {
    const ok = await ckConfirm({
      title: "לבטל את התיקון?",
      message: `${fix.title} — ${he(fix.count)} רשומות יחזרו למה שהיו. רשומות ששונו מאז התיקון יישארו כמו שהן.`,
      confirmLabel: "ביטול התיקון",
    });
    if (!ok) return;
    try {
      const res = await aiDataAuditApi.undo(fix.id);
      if (res.fix) setFixes((f) => f.map((x) => (x.id === fix.id ? res.fix! : x)));
      changedAny.current = true;
      toast.success(res.changed ? `התיקון בוטל · ${he(res.changed)} רשומות שונו מאז ונשארו כמו שהן` : "התיקון בוטל");
      void run([fix.code]);
    } catch (e) {
      toast.error(errorText(e, "הביטול נכשל"));
    }
  };

  /* ── conversation ── */
  const historyOf = (list: AgentChatMessage[]): AuditChatTurn[] =>
    list
      .filter((m) => (m.role === "user" || m.role === "agent") && !m.error && m.text)
      .map((m) => ({ role: m.role === "user" ? "user" : "assistant", text: m.text.slice(0, 8000) }));

  const sendChat = async (text: string) => {
    const history = historyOf(messages);
    setMessages((m) => [...m, { id: nextId(), role: "user", text }]);
    setChatBusy(true);
    try {
      const d = await aiDataAuditApi.chat(text, history);
      const acts: AgentChatAction[] = d.actions
        .filter((a): a is AuditFixActionDto => a.type === "fix")
        .map((a) => ({ ...a, primary: true, icon: "check", label: `${a.label}${a.amount ? ` · ${money(a.amount)}` : ""}` }));
      setMessages((m) => [...m, { id: nextId(), role: "agent", text: d.reply || "", steps: d.steps, actions: acts }]);
      if (d.actions.some((a) => a.type === "refresh_findings")) {
        aiDataAuditApi
          .state()
          .then((st) => {
            if (st.checks?.length) setChecks(st.checks);
          })
          .catch(() => undefined);
      }
    } catch (e) {
      setMessages((m) => [...m, { id: nextId(), role: "agent", error: true, text: errorText(e, "הסוכן לא הצליח לענות כרגע") }]);
    } finally {
      setChatBusy(false);
    }
  };

  // opened with the user's question — ask it once, on the conversation tab
  const askedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !canChat || !initialQuestion || askedRef.current === initialQuestion) return;
    askedRef.current = initialQuestion;
    setTab("chat");
    void sendChat(initialQuestion);
  }, [open, canChat, initialQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChatAction = async (a: AgentChatAction, msg: AgentChatMessage) => {
    if (a.type !== "fix") return;
    const code = String(a.code);
    const check = checks.find((c) => c.code === code) ?? {
      code,
      fixLabel: String(a.label),
      risk: (a.risk as "safe" | "review") ?? "review",
      title: String(a.title ?? ""),
    };
    const fix = await doFix(check, {
      ids: Array.isArray(a.ids) ? (a.ids as string[]) : undefined,
      recommended: Boolean(a.recommended),
      count: Number(a.count) || 0,
      amount: Number(a.amount) || 0,
    });
    if (!fix) return;
    setMessages((ms) =>
      ms
        .map((m) =>
          m.id === msg.id ? { ...m, actions: (m.actions ?? []).map((x) => (x === a ? { ...x, done: true, doneLabel: `תוקנו ${he(fix.count)}` } : x)) } : m,
        )
        .concat({
          id: nextId(),
          role: "note",
          text: `✓ ${fix.title}: ${he(fix.count)} תוקנו${fix.failed?.length ? `, ${fix.failed.length} לא (${fix.failed[0]!.error})` : ""}. אפשר לבטל ב«מה תוקן».`,
        }),
    );
  };

  const close = () => {
    if (changedAny.current) notifyDataChanged("data_audit");
    onClose?.();
  };

  const openItem = (i: AuditItemDto) => {
    if (changedAny.current) notifyDataChanged("data_audit");
    navigate(i.path);
  };

  /* ── derived ── */
  const withGaps = checks.filter((c) => c.count > 0);
  const clean = checks.filter((c) => !c.count && !c.error);
  const sorted = [...withGaps].sort((a, b) => a.impact - b.impact);
  const totals = {
    count: withGaps.reduce((a, c) => a + c.count, 0),
    // only checks where the amounts in the reports are wrong (not "linked to whom" / "how it was paid")
    amount: withGaps.filter((c) => c.moneyCheck).reduce((a, c) => a + (c.amount || 0), 0),
    recommended: withGaps.filter((c) => c.canFix && c.fixLabel).reduce((a, c) => a + (c.recommended || 0), 0),
    fixed: fixes.filter((f) => !f.undone).reduce((a, f) => a + f.count, 0),
  };

  const findingsPane = () => (
    <div className="de-main">
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {running && !checks.length ? (
        <div className="de-scan">
          <span className="de-scan__ic" aria-hidden="true">
            ✦
          </span>
          <b>הסוכן עובר על הנתונים של הארגון</b>
          <span className="de-muted">משווה עסקאות מול תשלומים, בודק שיוכים של לידים ונציגים, צורות תשלום, כפילויות ופרטי לקוחות…</span>
        </div>
      ) : null}
      {checks.length && !withGaps.length ? <Banner tone="success">לא נמצאו פערים. כל הבדיקות תקינות.</Banner> : null}
      {sorted.map((c) => (
        <CheckCard
          key={c.code}
          c={c}
          open={openCode === c.code}
          onToggle={() => toggle(c.code)}
          state={itemState[c.code]}
          onLoadMore={() => void loadItems(c.code, { append: true })}
          onSearch={(t) => {
            if (searchTimer.current) clearTimeout(searchTimer.current);
            searchTimer.current = setTimeout(() => void loadItems(c.code, { text: t }), 350);
            setItemState((s) => ({ ...s, [c.code]: { ...(s[c.code] ?? {}), text: t } }));
          }}
          onSelect={(how, id) => select(c.code, how, id)}
          onFix={(opts) => void doFix(c, opts)}
          onOpenItem={openItem}
          busy={busyFix || running}
        />
      ))}
      {clean.length ? (
        <div className="de-audit-clean">
          <span className="de-muted">✓ תקין:</span>
          {clean.map((c) => (
            <Tag key={c.code} tone="success">
              {c.title}
            </Tag>
          ))}
        </div>
      ) : null}
    </div>
  );

  const fixesPane = () => (
    <div className="de-main">
      {!fixes.length ? <div className="de-muted de-audit-empty">עוד לא תוקן כלום. כל תיקון שתאשרו יופיע כאן, עם אפשרות לבטל.</div> : null}
      {fixes.map((f) => (
        <section key={f.id} className={`de-audit-fix ${f.undone ? "is-undone" : ""}`}>
          <div className="de-audit-fix__h">
            <b>{f.title}</b>
            <Tag tone={f.undone ? "neutral" : "success"}>{f.undone ? "בוטל" : `${he(f.count)} תוקנו`}</Tag>
            {f.amount ? <Tag>{money(f.amount)}</Tag> : null}
            {f.failed?.length ? <Tag tone="warning">{f.failed.length} לא תוקנו</Tag> : null}
            <span className="ck-grow" />
            <span className="de-muted">
              {f.userName ? `${f.userName} · ` : ""}
              {new Date(f.at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
            </span>
            {f.canUndo ? (
              <Button size="sm" icon="undo" onClick={() => void undoFix(f)}>
                ביטול
              </Button>
            ) : null}
          </div>
          {f.items?.length ? (
            <div className="de-muted de-audit-fix__list">
              {f.items
                .slice(0, 8)
                .map((i) => `${i.label}${i.amount ? ` ${money(i.amount)}` : ""}`)
                .join(" · ")}
              {f.count > 8 ? ` · ועוד ${he(f.count - 8)}` : ""}
            </div>
          ) : null}
          {f.failed?.length ? (
            <div className="de-audit-fix__err">
              {f.failed
                .slice(0, 3)
                .map((x) => `${x.label}: ${x.error}`)
                .join(" · ")}
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );

  const chatPane = () => (
    <div className="de-main de-main--chat">
      {canChat ? (
        <AgentChat
          feedback={{ surface: "data_audit" }}
          messages={messages}
          busy={chatBusy}
          onSend={(t) => void sendChat(t)}
          onAction={(a, m) => void onChatAction(a, m)}
          draft={draft}
          setDraft={setDraft}
          starters={AUDIT_STARTERS}
          hint="מתארים בעיה במילים — הסוכן בודק, מסביר ומציע תיקון. שום דבר לא משתנה בלי אישור."
          emptyText="ספרו לסוכן מה לא מסתדר (למשל «הגבייה בחודש שעבר לא תואמת לעסקאות»), או בקשו ממנו לעבור על הכל."
          placeholder="למשל: «למה יש עסקאות בלי נציג?» · «תראה לי דוגמאות לכפילויות»"
          onClear={() => setMessages([])}
        />
      ) : (
        <Banner tone="info">{allowed("ai.use") ? "הסוכן לא מוגדר בארגון — הבדיקות והתיקונים זמינים בלשונית «מה מצאתי»." : "אין לך הרשאה לשוחח עם הסוכן — הבדיקות והתיקונים זמינים בלשונית «מה מצאתי»."}</Banner>
      )}
    </div>
  );

  const tabs = [
    { key: "findings", label: "מה מצאתי", count: withGaps.length || undefined, render: findingsPane },
    { key: "chat", label: "שיחה עם הסוכן", render: chatPane },
    { key: "fixes", label: "מה תוקן", count: fixes.filter((f) => !f.undone).length || undefined, render: fixesPane },
  ];

  const side = (
    <>
      <DeSection title="מה הסוכן בדק">
        <ul className="de-audit-side">
          {[...checks]
            .sort((a, b) => a.impact - b.impact)
            .map((c) => (
              <li key={c.code} className={c.count ? "has-gap" : ""}>
                <span aria-hidden="true">{c.error ? "!" : c.count ? "●" : "✓"}</span>
                <button
                  type="button"
                  className="ck-link"
                  onClick={() => {
                    setTab("findings");
                    if (c.count && openCode !== c.code) toggle(c.code);
                  }}
                >
                  {c.title}
                </button>
                <b>{c.count ? he(c.count) : ""}</b>
              </li>
            ))}
        </ul>
      </DeSection>
      <DeSection title="איך זה עובד">
        <KV
          items={[
            { label: "בדיקה", value: "קריאה בלבד — לא משנה כלום" },
            { label: "תיקון", value: "רק מה שאישרתם, דרך המסלולים של המערכת" },
            { label: "ביטול", value: "כל תיקון אפשר לבטל ב«מה תוקן»" },
            { label: "שיחה", value: "אפשר לתאר בעיה במילים — הסוכן יחקור" },
          ]}
        />
      </DeSection>
    </>
  );

  return (
    <EntityCard
      open={open}
      onClose={close}
      width={1320}
      title="בדיקת נתונים"
      subtitle={<span>הסוכן משווה עסקאות, תשלומים, לידים ולקוחות — מוצא פערים, מסביר ומתקן באישורכם</span>}
      avatarText="✦"
      tags={[
        running
          ? { label: "בודק…", tone: "violet" }
          : checks.length
            ? { label: withGaps.length ? `${he(withGaps.length)} סוגי פערים` : "הכל תקין", tone: withGaps.length ? "warning" : "success" }
            : null,
      ].filter(Boolean)}
      actions={[
        { key: "rerun", label: running ? "בודק…" : "בדיקה מחדש", icon: "refresh", disabled: running, onClick: () => void run() },
        ...(canChat ? [{ key: "ask", label: "לשאול את הסוכן", icon: "sparkle", primary: tab !== "chat", onClick: () => setTab("chat") }] : []),
      ]}
      kpis={[
        { key: "gaps", label: "רשומות עם פער", value: he(totals.count), attn: totals.count > 0, onClick: () => setTab("findings") },
        { key: "rec", label: "מומלץ לתקן", value: he(totals.recommended), sub: totals.recommended ? "תיקון בטוח" : null },
        { key: "amount", label: "כסף שמוצג לא נכון", value: money(totals.amount), sub: "עסקאות / תשלומים / יתרות בדוחות" },
        { key: "fixed", label: "תוקנו", value: he(totals.fixed), onClick: () => setTab("fixes") },
      ]}
      tabs={tabs}
      tab={tab}
      onTabChange={setTab}
      detailsTop={<div className="de-side-col">{side}</div>}
    />
  );
}

/** /ai/data-audit — the card open on its own route; closing goes back. */
export function DataAuditAgentRoute() {
  const navigate = useNavigate();
  return (
    <DataAuditAgent
      open
      onClose={() => {
        if (window.history.length > 1) navigate(-1);
        else navigate("/");
      }}
    />
  );
}

export default DataAuditAgent;
